import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useQuery } from 'react-query';
import { getScenario } from 'src/api/scenario';
import { useParams } from 'react-router';
import { ProjectionVariant, QKeys } from 'src/api/types';
import Meta from 'src/components/Meta';
import { VariantValues, PageCard, PageChartSkeleton, PageContainer, Spacer, Divider, Poppins } from 'src/common';
import { useGaEventPageView } from '../../utils/gaEvent';
import { Head } from './comps';
import { useVersionV2 } from 'src/hooks/useVersion';
import useActionsApp from 'src/redux/actionHooks/useActionsApp';
import {
  AuditSection,
  ExceedanceSection,
  IncidentsSection,
  RiskManagementSection,
  RiskStatementSection,
} from './sections';
import { ObjectKind, useScenarioQuery } from 'src/graphql/_generated';
import { isQueryLoading } from 'src/graphql';
import ActionsSection from 'src/components/ActionsSection';
import { LABELS } from 'src/components/labels';

const ChartBox = styled(PageCard)`
  padding-bottom: 0;
`;

const ScenarioPage = () => {
  const { scenarioId, assessmentId } = useParams<{ scenarioId: string; assessmentId: string }>();
  const [isLogarithmic, setIsLogarithmic] = useState(true);

  const { versionId, version, isLoading: isVersionLoading } = useVersionV2({ assessmentId: assessmentId! });

  const { setLocationPaths } = useActionsApp();

  const { isLoading } = useQuery(
    [QKeys.Scenario, { scenarioId, version: versionId, variant: ProjectionVariant.live }],
    getScenario,
  );

  const {
    data: { scenario: scenarioGql } = {},
    loading: isLoadingScenarioGql,
    networkStatus,
    error,
  } = useScenarioQuery({ variables: { id: scenarioId!, versionId } });

  const isLoadingGql = isQueryLoading({ networkStatus, data: scenarioGql, loading: isLoadingScenarioGql });

  useEffect(() => {
    if (scenarioGql) {
      setLocationPaths({
        assessment: { id: scenarioGql.assessment.id, name: scenarioGql.assessment.name },
        scenario: { id: scenarioGql.id, name: scenarioGql.name! },
      });
    }
    return () => {
      setLocationPaths({ assessment: undefined, scenario: undefined });
    };
  }, [scenarioGql]);

  useGaEventPageView('Cydea | Scenario');

  const showAllProjected = !!scenarioGql?.riskManagementPlans
    ?.filter((plan) => plan.control && plan.control.status !== 'Abandoned' && plan.control.status !== 'Retired')
    ?.flatMap((plan) => plan.modifiers)
    ?.filter((modifier) => modifier.type !== 'NOTE_TYPE')?.length;

  const loading = isLoading || isLoadingGql || isVersionLoading;

  return (
    <>
      <Meta title={`Cydea | ${scenarioGql?.name}`} feedbackScript />
      <PageContainer data-cy="scenario-page">
        {error && <Poppins color="error">{error.message}</Poppins>}
        {loading ? (
          <PageChartSkeleton />
        ) : (
          <>
            {scenarioGql && (
              <>
                <ChartBox>
                  <Head
                    scenario={scenarioGql}
                    isLogarithmic={isLogarithmic}
                    version={version}
                    expectedLossComponent={
                      <VariantValues
                        flat
                        value={
                          showAllProjected
                            ? {
                                base: scenarioGql.baseRisk?.expectedLoss,
                                live: scenarioGql.liveRisk?.expectedLoss,
                                cpmplete: scenarioGql.projectedRisk?.expectedLoss,
                              }
                            : scenarioGql.liveRisk.expectedLoss
                        }
                        tooltipText={{
                          base: LABELS.riskBase,
                          live: LABELS.riskLive,
                          cpmplete: LABELS.riskComplete,
                        }}
                      />
                    }
                  />
                  <Spacer $px={20} />
                  <Divider />
                  <ExceedanceSection
                    isLogarithmic={isLogarithmic}
                    setIsLogarithmic={setIsLogarithmic}
                    showAllProjected={showAllProjected}
                    scenario={scenarioGql}
                    version={version}
                  />
                  <Divider />
                  <RiskStatementSection scenario={scenarioGql} showAllProjected={showAllProjected} />
                  <Divider />
                  <RiskManagementSection scenario={scenarioGql} />
                  <Divider />
                  <IncidentsSection scenario={scenarioGql} />
                  <Divider />
                  <ActionsSection
                    objectType={ObjectKind.Scenario}
                    objectId={scenarioGql.id}
                    name={scenarioGql.name || ''}
                  />
                  <Divider />
                  <AuditSection versionId={version?.id} scenario={scenarioGql} />
                </ChartBox>
              </>
            )}
          </>
        )}
      </PageContainer>
    </>
  );
};

export default ScenarioPage;
